import { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import dayjs from 'dayjs';
import './DateRangePicker.css';

export interface DateRange {
  start_date: string;
  end_date: string;
}

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  maxDate?: string; // Maximum selectable date (YYYY-MM-DD)
  fromType?: 'default' | 'tokenReport';
}

// Generate calendar days for a given month
const generateCalendarDays = (year: number, month: number): (number | null)[] => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startDayOfWeek = firstDay.getDay(); // 0 = Sunday

  const days: (number | null)[] = [];
  // Empty cells for days before the first day of month
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push(null);
  }
  // Days of the month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }
  return days;
};

export default function DateRangePicker({
  value,
  onChange,
  disabled = false,
  className = '',
  placeholder = 'Select date range',
  maxDate,
  fromType = 'default',
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [viewDate, setViewDate] = useState(dayjs()); // Current view month
  const [hoverDate, setHoverDate] = useState<string | null>(null); // For range preview
  const [selecting, setSelecting] = useState<'start' | 'end' | null>(null); // Which date is being selected

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position
  const updateDropdownPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // 下拉框最小宽度
    const dropdownWidth = 320;
    // 判断右侧是否溢出视口
    const overflowRight = rect.left + dropdownWidth > window.innerWidth;
    setDropdownStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      // 溢出时右对齐到触发器右边缘，否则左对齐
      left: overflowRight ? rect.right - dropdownWidth : rect.left,
      zIndex: 9999,
    });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideTrigger = containerRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideTrigger && !insideDropdown) {
        setIsOpen(false);
        setSelecting(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    const handleUpdate = () => updateDropdownPosition();
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updateDropdownPosition]);

  const handleToggle = () => {
    if (disabled) return;
    if (!isOpen) {
      updateDropdownPosition();
      // Show start date's month if available, otherwise show current month
      if (value.start_date) {
        setViewDate(dayjs(value.start_date));
      } else {
        setViewDate(dayjs());
      }
    }
    setIsOpen(prev => !prev);
    setSelecting(null);
  };

  // Format display value
  const displayValue =
    value.start_date && value.end_date ? `${value.start_date} ~ ${value.end_date}` : '';

  // Check if a date is within the selected range
  const isInRange = (dateStr: string): boolean => {
    if (!value.start_date || !value.end_date) return false;
    const date = dayjs(dateStr);
    const start = dayjs(value.start_date);
    const end = dayjs(value.end_date);
    return date.isAfter(start, 'day') && date.isBefore(end, 'day');
  };

  // Check if a date is the start or end of range
  const isRangeStart = (dateStr: string): boolean => value.start_date === dateStr;
  const isRangeEnd = (dateStr: string): boolean => value.end_date === dateStr;

  // Check if date should show hover preview
  const isInHoverRange = (dateStr: string): boolean => {
    if (!hoverDate || !value.start_date) return false;
    if (selecting !== 'end') return false;
    const date = dayjs(dateStr);
    const start = dayjs(value.start_date);
    const hover = dayjs(hoverDate);
    return date.isAfter(start, 'day') && date.isBefore(hover, 'day');
  };

  // Handle date click
  const handleDateClick = (day: number) => {
    const dateStr = viewDate.date(day).format('YYYY-MM-DD');

    if (maxDate && dayjs(dateStr).isAfter(maxDate, 'day')) {
      return; // Cannot select date after maxDate
    }

    if (!selecting || selecting === 'start') {
      // Start new selection
      onChange({ start_date: dateStr, end_date: '' });
      setSelecting('end');
    } else {
      // Complete selection
      const startDate = dayjs(value.start_date);
      const clickedDate = dayjs(dateStr);

      if (clickedDate.isBefore(startDate, 'day')) {
        // If clicked before start, swap them
        onChange({ start_date: dateStr, end_date: value.start_date });
      } else {
        onChange({ start_date: value.start_date, end_date: dateStr });
      }
      setSelecting(null);
      setIsOpen(false);
    }
  };

  // Handle date hover for preview
  const handleDateHover = (day: number | null) => {
    if (day === null) {
      setHoverDate(null);
      return;
    }
    const dateStr = viewDate.date(day).format('YYYY-MM-DD');
    setHoverDate(dateStr);
  };

  // Navigate to previous/next month
  const goToPrevMonth = () => setViewDate(prev => prev.subtract(1, 'month'));
  const goToNextMonth = () => setViewDate(prev => prev.add(1, 'month'));

  // Calendar data
  const year = viewDate.year();
  const month = viewDate.month();
  const calendarDays = generateCalendarDays(year, month);
  const monthName = viewDate.format('MMMM YYYY');
  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Check if date is disabled (after maxDate)
  const isDateDisabled = (day: number): boolean => {
    if (!maxDate) return false;
    const dateStr = viewDate.date(day).format('YYYY-MM-DD');
    return dayjs(dateStr).isAfter(maxDate, 'day');
  };

  // Render dropdown via Portal
  const dropdownContent = isOpen
    ? ReactDOM.createPortal(
        <div ref={dropdownRef} className="date-range-dropdown" style={dropdownStyle}>
          <div className="date-range-picker-panel">
            {/* Header */}
            <div className="picker-header">
              <button
                className={`nav-btn ${fromType === 'tokenReport' ? 'nav-btn-user-style' : ''}`}
                onClick={goToPrevMonth}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <span className="month-year">{monthName}</span>
              <button
                className={`nav-btn ${fromType === 'tokenReport' ? 'nav-btn-user-style' : ''}`}
                onClick={goToNextMonth}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>

            {/* Weekday headers */}
            <div className="weekday-header">
              {weekDays.map(day => (
                <div key={day} className="weekday-cell">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="calendar-grid">
              {calendarDays.map((day, index) => {
                if (day === null) {
                  return <div key={`empty-${index}`} className="calendar-cell empty" />;
                }

                const dateStr = viewDate.date(day).format('YYYY-MM-DD');
                const disabled = isDateDisabled(day);
                const inRange = isInRange(dateStr);
                const rangeStart = isRangeStart(dateStr);
                const rangeEnd = isRangeEnd(dateStr);
                const inHover = isInHoverRange(dateStr);

                return (
                  <div
                    key={day}
                    className={`calendar-cell ${disabled ? 'disabled' : ''} ${
                      rangeStart || rangeEnd ? 'selected' : ''
                    } ${
                      inRange || inHover ? 'in-range' : ''
                    } ${rangeStart ? 'range-start' : ''} ${rangeEnd ? 'range-end' : ''}`}
                    onClick={() => !disabled && handleDateClick(day)}
                    onMouseEnter={() => !disabled && handleDateHover(day)}
                    onMouseLeave={() => setHoverDate(null)}
                  >
                    <span className="date-number">{day}</span>
                  </div>
                );
              })}
            </div>

            {/* Footer with shortcuts */}
            <div className="picker-footer">
              <button
                className="shortcut-btn"
                onClick={() => {
                  const today = dayjs();
                  onChange({
                    start_date: today.format('YYYY-MM-DD'),
                    end_date: today.format('YYYY-MM-DD'),
                  });
                  setIsOpen(false);
                  setSelecting(null);
                }}
              >
                Today
              </button>
              <button
                className="shortcut-btn"
                onClick={() => {
                  const today = dayjs();
                  onChange({
                    start_date: today.subtract(7, 'day').format('YYYY-MM-DD'),
                    end_date: today.format('YYYY-MM-DD'),
                  });
                  setIsOpen(false);
                  setSelecting(null);
                }}
              >
                Last 7 Days
              </button>
              <button
                className="shortcut-btn"
                onClick={() => {
                  const today = dayjs();
                  onChange({
                    start_date: today.subtract(30, 'day').format('YYYY-MM-DD'),
                    end_date: today.format('YYYY-MM-DD'),
                  });
                  setIsOpen(false);
                  setSelecting(null);
                }}
              >
                Last 30 Days
              </button>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div
      ref={containerRef}
      className={`date-range-picker ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${className}`}
    >
      {/* Trigger */}
      <div ref={triggerRef} className="date-range-trigger" onClick={handleToggle}>
        <span className={`date-range-value ${!displayValue ? 'placeholder' : ''}`}>
          {displayValue || placeholder}
        </span>
        <span className="date-range-icon">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </span>
      </div>

      {dropdownContent}
    </div>
  );
}
